/**
 * Dashboard forensique — Audit trail HTTP complet (Phase 3, 2026-08-27).
 *
 * Réservé aux super_admin. Alimenté par `core.LogActivite` (rempli à chaque
 * requête HTTP par le middleware `AuditActivityMiddleware`).
 *
 * Trois vues :
 *   1. Vue Globale : KPIs, top users, top paths, alertes, distribution 24h
 *   2. Vue Journal : liste paginée filtrable (user, path, method, tag)
 *   3. Vue Utilisateur : drill-down complet sur un agent
 */
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Shield, RefreshCw, Download, User, AlertTriangle, Filter,
  Clock, Globe, ChevronDown, Activity, TrendingUp,
} from 'lucide-react';
import {
  useAuditActiviteStatsQuery,
  useAuditActiviteListeQuery,
  useAuditActiviteUserQuery,
  useJournalNotesQuery,
  AuditActiviteLigne,
  JournalNoteLigne,
  JournalNotesParams,
} from '../../services/coreApi';
import { RootState } from '../../store';

type Vue = 'globale' | 'journal' | 'notes' | 'user';

const AuditAgents: React.FC = () => {
  const user = useSelector((s: RootState) => s.auth.user);
  const [vue, setVue] = useState<Vue>('globale');
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  if (user?.role !== 'super_admin') {
    return (
      <div className="p-8">
        <div className="max-w-xl mx-auto bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-red-800 dark:text-red-300">
          <div className="flex items-center gap-2 mb-2 font-semibold">
            <Shield size={20} /> Accès refusé
          </div>
          <div className="text-sm">Réservé aux super-administrateurs.</div>
        </div>
      </div>
    );
  }

  const openUser = (uid: number) => { setSelectedUserId(uid); setVue('user'); };

  return (
    <div className="p-4 md:p-6 space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Activity className="text-amber-500" size={24} /> Audit — Activité des agents
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Trace HTTP complète du back-office : chaque requête (URL, méthode, statut,
            durée) est journalisée avec l'utilisateur, l'IP et l'user-agent.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
        <TabBtn on={vue === 'globale'} onClick={() => setVue('globale')}>Vue globale</TabBtn>
        <TabBtn on={vue === 'journal'} onClick={() => setVue('journal')}>Journal</TabBtn>
        <TabBtn on={vue === 'notes'} onClick={() => setVue('notes')}>Journal des notes</TabBtn>
        <TabBtn on={vue === 'user'} onClick={() => setVue('user')} disabled={!selectedUserId}>
          Utilisateur {selectedUserId ? `#${selectedUserId}` : ''}
        </TabBtn>
      </div>

      {vue === 'globale' && <VueGlobale onOpenUser={openUser} />}
      {vue === 'journal' && <VueJournal onOpenUser={openUser} />}
      {vue === 'notes' && <VueJournalNotes />}
      {vue === 'user' && selectedUserId && <VueUser userId={selectedUserId} />}
    </div>
  );
};

// ─── Composants réutilisables ───────────────────────────────────────────────

function TabBtn({ children, on, onClick, disabled }: {
  children: React.ReactNode; on: boolean; onClick: () => void; disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`px-3 py-2 text-sm border-b-2 -mb-px transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
        on
          ? 'border-amber-500 text-amber-600 dark:text-amber-400 font-medium'
          : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
      }`}
    >
      {children}
    </button>
  );
}

function KpiCard({ label, value, tone = 'neutral', hint }: {
  label: string; value: number | string; tone?: 'neutral' | 'warn' | 'ok'; hint?: string;
}) {
  return (
    <div className={`rounded-xl border p-4 ${
      tone === 'warn' ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-800'
        : tone === 'ok' ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-800'
        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
    }`}>
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      {hint && <div className="text-xs text-amber-700 dark:text-amber-300 mt-1">{hint}</div>}
    </div>
  );
}

function methodColor(m: string) {
  if (m === 'GET') return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300';
  if (m === 'POST') return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300';
  if (m === 'PUT' || m === 'PATCH') return 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
  if (m === 'DELETE') return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
  return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200';
}

function statutColor(s: number | null) {
  if (!s) return 'text-gray-400';
  if (s >= 500) return 'text-red-600 dark:text-red-400';
  if (s >= 400) return 'text-amber-600 dark:text-amber-400';
  if (s >= 300) return 'text-blue-600 dark:text-blue-400';
  return 'text-green-600 dark:text-green-400';
}

// ─── Vue globale ────────────────────────────────────────────────────────────

function VueGlobale({ onOpenUser }: { onOpenUser: (uid: number) => void }) {
  const [depuis, setDepuis] = useState('');
  const [jusqu, setJusqu] = useState('');
  const { data, isFetching, refetch } = useAuditActiviteStatsQuery(
    { depuis: depuis || undefined, jusqu: jusqu || undefined },
    { pollingInterval: 60_000 },
  );

  const maxHeure = useMemo(
    () => Math.max(1, ...(data?.par_heure || []).map(h => h.nb)),
    [data],
  );

  return (
    <div className="space-y-4">
      {/* Filtres période */}
      <div className="flex items-end gap-3 flex-wrap bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3">
        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Depuis</label>
          <input type="date" value={depuis} onChange={(e) => setDepuis(e.target.value)}
            className="px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Jusqu'au</label>
          <input type="date" value={jusqu} onChange={(e) => setJusqu(e.target.value)}
            className="px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900" />
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 ml-2">
          Défaut : 7 derniers jours
        </div>
        <button onClick={() => refetch()} disabled={isFetching}
          className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700">
          <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} /> Rafraîchir
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Requêtes journalisées" value={data?.total || 0} />
        <KpiCard label="Nocturnes (22h-06h)" value={data?.par_tag.nuit || 0}
          tone={(data?.par_tag.nuit || 0) > 0 ? 'warn' : 'neutral'} />
        <KpiCard label="Mobile" value={data?.par_tag.mobile || 0} />
        <KpiCard label="Endpoints sensibles" value={data?.par_tag.sensible || 0}
          tone={(data?.par_tag.sensible || 0) > 100 ? 'warn' : 'neutral'} />
      </div>

      {/* Alertes */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="🔴 Accès sensible la nuit" value={data?.alertes['accès_sensible_nuit'] || 0}
          tone={(data?.alertes['accès_sensible_nuit'] || 0) > 0 ? 'warn' : 'ok'} />
        <KpiCard label="🔴 Destructif + sensible" value={data?.alertes['destructif_sensible'] || 0}
          tone={(data?.alertes['destructif_sensible'] || 0) > 0 ? 'warn' : 'ok'} />
        <KpiCard label="Erreurs 4xx" value={data?.alertes.nb_4xx || 0} />
        <KpiCard label="Erreurs 5xx" value={data?.alertes.nb_5xx || 0}
          tone={(data?.alertes.nb_5xx || 0) > 0 ? 'warn' : 'ok'} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top users */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 mb-3">
            <User size={16} className="text-amber-500" />
            <div className="font-semibold text-gray-900 dark:text-gray-100">Top utilisateurs</div>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              (cliquez pour ouvrir la timeline)
            </span>
          </div>
          {(data?.top_users || []).length === 0 && (
            <div className="text-sm text-gray-500 dark:text-gray-400">Aucune activité.</div>
          )}
          <div className="space-y-1">
            {(data?.top_users || []).map(u => (
              <button
                key={u.user_id}
                onClick={() => u.user_id && onOpenUser(u.user_id)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40 text-left"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {u.nom_complet}
                    <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                      {u.username} · {u.role}
                    </span>
                  </div>
                </div>
                <div className={`shrink-0 px-2 py-0.5 rounded text-xs font-semibold ${
                  u.nb >= 500 ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                  : u.nb >= 100 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                }`}>
                  {u.nb.toLocaleString()}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Top paths */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp size={16} className="text-blue-500" />
            <div className="font-semibold text-gray-900 dark:text-gray-100">Endpoints les plus appelés</div>
          </div>
          <div className="space-y-1">
            {(data?.top_paths || []).map((p, i) => (
              <div key={i} className="flex items-center justify-between gap-2 px-3 py-1.5">
                <div className="text-xs text-gray-800 dark:text-gray-200 font-mono truncate">
                  {p.path}
                </div>
                <div className="shrink-0 text-xs text-gray-600 dark:text-gray-400 font-semibold">
                  {p.nb.toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Distribution 24h */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Clock size={16} className="text-amber-500" />
          <div className="font-semibold text-gray-900 dark:text-gray-100">
            Distribution par heure (dernières 24h)
          </div>
        </div>
        <div className="flex items-end gap-1 h-32">
          {(data?.par_heure || []).map(h => {
            const isNight = h.heure >= 22 || h.heure < 6;
            const height = Math.max(2, Math.round((h.nb / maxHeure) * 120));
            return (
              <div key={h.heure} className="flex-1 flex flex-col items-center gap-1" title={`${h.heure}h — ${h.nb}`}>
                <div
                  className={`w-full rounded-t ${isNight ? 'bg-amber-400' : 'bg-blue-400'}`}
                  style={{ height: `${height}px` }}
                />
                <div className="text-[10px] text-gray-500 dark:text-gray-400">{h.heure}</div>
              </div>
            );
          })}
        </div>
        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-4">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded bg-blue-400 inline-block" /> Jour (06h-22h)</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded bg-amber-400 inline-block" /> Nuit (22h-06h)</span>
        </div>
      </div>
    </div>
  );
}

// ─── Vue journal ────────────────────────────────────────────────────────────

function VueJournal({ onOpenUser }: { onOpenUser: (uid: number) => void }) {
  const now = new Date();
  const yest = new Date(now.getTime() - 24 * 3600 * 1000);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  const [depuis, setDepuis] = useState(iso(yest));
  const [jusqu, setJusqu] = useState(iso(now));
  const [userIdFilter, setUserIdFilter] = useState('');
  const [method, setMethod] = useState('');
  const [pathFilter, setPathFilter] = useState('');
  const [tag, setTag] = useState('');
  const [limit, setLimit] = useState(500);
  const [openIds, setOpenIds] = useState<Record<number, boolean>>({});

  const { data, isFetching, refetch } = useAuditActiviteListeQuery({
    depuis, jusqu,
    user_id: userIdFilter ? Number(userIdFilter) : undefined,
    method: method || undefined,
    path: pathFilter || undefined,
    tag: tag || undefined,
    limit,
  });

  const exportCSV = () => {
    const items = data?.items || [];
    if (!items.length) return;
    const header = ['date', 'user', 'role', 'method', 'path', 'query', 'statut', 'duree_ms', 'ip', 'tags', 'ua', 'payload'];
    const rows = items.map(l => [
      l.date, `"${l.user_nom} (${l.user_username})"`, l.user_role,
      l.method, l.path, l.query, String(l.statut_http ?? ''), String(l.duree_ms ?? ''),
      l.ip, l.tags, `"${(l.user_agent || '').replace(/"/g, '""')}"`,
      `"${(l.payload_resume || '').replace(/"/g, '""')}"`,
    ].join(','));
    const blob = new Blob(['﻿' + [header.join(','), ...rows].join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit_agents_${depuis}_${jusqu}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const items = data?.items || [];

  return (
    <div className="space-y-4">
      {/* Filtres */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center gap-2 mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
          <Filter size={14} /> Filtres
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <FF label="Depuis"><input type="date" value={depuis} onChange={(e) => setDepuis(e.target.value)} className={ii} /></FF>
          <FF label="Jusqu'au"><input type="date" value={jusqu} onChange={(e) => setJusqu(e.target.value)} className={ii} /></FF>
          <FF label="ID utilisateur"><input type="number" value={userIdFilter} onChange={(e) => setUserIdFilter(e.target.value)} placeholder="tous" className={ii} /></FF>
          <FF label="Méthode">
            <select value={method} onChange={(e) => setMethod(e.target.value)} className={ii}>
              <option value="">toutes</option>
              {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </FF>
          <FF label="Chemin (contains)"><input value={pathFilter} onChange={(e) => setPathFilter(e.target.value)} placeholder="/api/..." className={ii} /></FF>
          <FF label="Tag">
            <select value={tag} onChange={(e) => setTag(e.target.value)} className={ii}>
              <option value="">tous</option>
              <option value="sensible">sensible</option>
              <option value="destructif">destructif</option>
              <option value="nuit">nuit</option>
              <option value="mobile">mobile</option>
            </select>
          </FF>
          <FF label="Limite">
            <select value={limit} onChange={(e) => setLimit(Number(e.target.value))} className={ii}>
              {[100, 500, 1000].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </FF>
          <FF label="&nbsp;">
            <div className="flex gap-2">
              <button onClick={() => refetch()} disabled={isFetching}
                className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700">
                <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
              </button>
              <button onClick={exportCSV} disabled={!items.length}
                className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 text-sm rounded bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50">
                <Download size={14} /> CSV
              </button>
            </div>
          </FF>
        </div>
      </div>

      {/* Liste */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
        <div className="p-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Journal</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {data ? `${items.length} sur ${data.total}` : ''}
          </div>
        </div>
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {items.length === 0 && (
            <li className="p-6 text-sm text-gray-500 dark:text-gray-400 text-center">
              Aucune ligne sur cette période. (Le journal ne remonte pas avant le déploiement Phase 3 — 2026-08-27.)
            </li>
          )}
          {items.map(l => (
            <LigneJournal key={l.id} l={l}
              open={!!openIds[l.id]}
              onToggle={() => setOpenIds(s => ({ ...s, [l.id]: !s[l.id] }))}
              onOpenUser={onOpenUser}
            />
          ))}
        </ul>
      </div>
    </div>
  );
}

function LigneJournal({ l, open, onToggle, onOpenUser }: {
  l: AuditActiviteLigne; open: boolean; onToggle: () => void; onOpenUser: (uid: number) => void;
}) {
  const isNight = (l.tags || '').includes('nuit');
  const isSens = (l.tags || '').includes('sensible');
  const isDest = (l.tags || '').includes('destructif');
  const isMob = (l.tags || '').includes('mobile');
  const d = new Date(l.date);

  return (
    <li className={`p-3 ${isSens && isNight ? 'bg-red-50/50 dark:bg-red-900/10' : isNight ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''}`}>
      <div className="flex items-start gap-2 flex-wrap md:flex-nowrap">
        <div className="flex items-center gap-2 shrink-0">
          <span className={`px-1.5 py-0.5 text-xs font-mono font-semibold rounded ${methodColor(l.method)}`}>
            {l.method}
          </span>
          <span className={`text-xs font-mono ${statutColor(l.statut_http)}`}>
            {l.statut_http ?? '—'}
          </span>
          {typeof l.duree_ms === 'number' && (
            <span className="text-[11px] text-gray-500 dark:text-gray-400">
              {l.duree_ms}ms
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-mono text-gray-800 dark:text-gray-200 break-all">
            {l.path}
            {l.query && <span className="text-gray-500">?{l.query}</span>}
          </div>
          <div className="mt-1 flex items-center gap-2 flex-wrap text-xs text-gray-500 dark:text-gray-400">
            {l.user_id ? (
              <button onClick={() => onOpenUser(l.user_id!)}
                className="text-amber-600 dark:text-amber-400 hover:underline inline-flex items-center gap-1">
                <User size={11} /> {l.user_nom}
                <span className="text-gray-400">({l.user_username} · {l.user_role})</span>
              </button>
            ) : <span className="italic">anonyme</span>}
            <span className="inline-flex items-center gap-1"><Clock size={11} /> {d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'medium' })}</span>
            {l.ip && <span className="inline-flex items-center gap-1"><Globe size={11} /> {l.ip}</span>}
            {isNight && <span className="px-1 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">🌙</span>}
            {isSens && <span className="px-1 py-0.5 rounded bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200">sensible</span>}
            {isDest && <span className="px-1 py-0.5 rounded bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200">destructif</span>}
            {isMob && <span className="px-1 py-0.5 rounded bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">📱</span>}
            {(l.payload_resume || l.user_agent) && (
              <button onClick={onToggle} className="ml-auto text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1">
                <ChevronDown size={11} className={`transition-transform ${open ? 'rotate-180' : ''}`} /> détail
              </button>
            )}
          </div>
          {open && (
            <div className="mt-2 space-y-1">
              {l.payload_resume && (
                <pre className="text-xs bg-gray-50 dark:bg-gray-900 p-2 rounded border border-gray-200 dark:border-gray-700 overflow-x-auto max-w-full whitespace-pre-wrap break-words">
                  <span className="text-gray-500">payload : </span>{l.payload_resume}
                </pre>
              )}
              {l.user_agent && (
                <pre className="text-xs bg-gray-50 dark:bg-gray-900 p-2 rounded border border-gray-200 dark:border-gray-700 overflow-x-auto max-w-full whitespace-pre-wrap break-words">
                  <span className="text-gray-500">UA : </span>{l.user_agent}
                </pre>
              )}
              {l.referer && (
                <div className="text-xs text-gray-500">referer : <span className="font-mono">{l.referer}</span></div>
              )}
              {l.view_name && (
                <div className="text-xs text-gray-500">view : <span className="font-mono">{l.view_name}</span></div>
              )}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

// ─── Journal des notes (trigger PostgreSQL, 2026-09-11) ─────────────────────

const LIBELLES_CHAMPS: Record<string, string> = {
  note1: 'Devoir 1', absent1: 'Absent D1', note2: 'Devoir 2', absent2: 'Absent D2',
  moyenne_devoirs: 'Moyenne devoirs', note_examen: 'Examen', absent: 'Absent examen',
  est_rattrapage: 'Rattrapage', semestre: 'Semestre', annee_academique: 'Année',
  verrouille: 'Verrouillé', date_limite: 'Date limite', etudiant_id: 'Étudiant (id)',
  matiere_id: 'Matière (id)', saisi_par_id: 'Saisi par', modifie_par_id: 'Modifié par',
};
const CHAMPS_NUMERIQUES = new Set(['note1', 'note2', 'moyenne_devoirs', 'note_examen']);
const LIBELLES_OPERATIONS: Record<string, string> = { I: 'Création', U: 'Modification', D: 'Suppression' };

function valeurJournal(champ: string, v: string | number | boolean | null): string {
  if (v === null || v === undefined || v === '') return '—';
  if (v === true) return 'oui';
  if (v === false) return 'non';
  if (CHAMPS_NUMERIQUES.has(champ)) {
    const n = Number(v);
    if (!Number.isNaN(n)) return n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return String(v);
}

function VueJournalNotes() {
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  const maintenant = new Date();
  const initial: JournalNotesParams = {
    depuis: iso(new Date(maintenant.getTime() - 7 * 24 * 3600 * 1000)),
    jusqu: iso(maintenant),
    limit: 500,
  };
  const [saisie, setSaisie] = useState<JournalNotesParams>(initial);
  const [filtres, setFiltres] = useState<JournalNotesParams>(initial);
  const { data, isFetching, error, refetch } = useJournalNotesQuery(filtres);

  const maj = (champ: keyof JournalNotesParams, valeur: string) => {
    const v = valeur === '' ? undefined : (champ === 'user_id' || champ === 'limit' ? Number(valeur) : valeur);
    setSaisie(s => ({ ...s, [champ]: v } as JournalNotesParams));
  };
  const rechercher = (e: React.FormEvent) => {
    e.preventDefault();
    setFiltres({
      ...saisie,
      matricule: saisie.matricule?.trim() || undefined,
      matiere: saisie.matiere?.trim() || undefined,
    });
  };

  const items = data?.items || [];
  const detail = (error as any)?.data?.detail as string | undefined;

  const exportCSV = () => {
    if (!items.length) return;
    const q = (s: string) => `"${(s || '').replace(/"/g, '""')}"`;
    const header = ['date', 'operation', 'notes', 'matricule', 'etudiant', 'classe', 'matiere', 'semestre',
      'changements', 'utilisateur', 'ip', 'chemin', 'origine'];
    const rows = items.map(l => [
      l.date, LIBELLES_OPERATIONS[l.operation] || l.operation,
      l.table + (l.est_rattrapage ? ' (rattrapage)' : ''),
      l.etudiant.matricule, q(l.etudiant.nom), l.etudiant.classe, q(`${l.matiere.code} ${l.matiere.nom}`),
      l.semestre || '',
      q(l.changements.map(c => `${LIBELLES_CHAMPS[c.champ] || c.champ} : ${valeurJournal(c.champ, c.avant)} -> ${valeurJournal(c.champ, c.apres)}`).join(' ; ')),
      q(l.utilisateur ? `${l.utilisateur.username} (${l.utilisateur.role})` : 'hors application'),
      l.adresse_ip, q(l.chemin), q(l.origine),
    ].join(','));
    const blob = new Blob(['﻿' + [header.join(','), ...rows].join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `journal_notes_${filtres.depuis || ''}_${filtres.jusqu || ''}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const bouton = 'inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50';

  return (
    <div className="space-y-4">
      <form onSubmit={rechercher} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <Filter size={14} /> Filtres
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Chaque création, modification ou suppression de note, quel que soit l'écran ou le traitement.
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <FF label="Depuis"><input type="date" value={saisie.depuis || ''} onChange={(e) => maj('depuis', e.target.value)} className={ii} /></FF>
          <FF label="Jusqu'au"><input type="date" value={saisie.jusqu || ''} onChange={(e) => maj('jusqu', e.target.value)} className={ii} /></FF>
          <FF label="Matricule"><input value={saisie.matricule || ''} onChange={(e) => maj('matricule', e.target.value)} placeholder="tous" className={ii} /></FF>
          <FF label="Matière (code ou nom)"><input value={saisie.matiere || ''} onChange={(e) => maj('matiere', e.target.value)} placeholder="toutes" className={ii} /></FF>
          <FF label="ID utilisateur"><input type="number" value={saisie.user_id ?? ''} onChange={(e) => maj('user_id', e.target.value)} placeholder="tous" className={ii} /></FF>
          <FF label="Notes">
            <select value={saisie.table || ''} onChange={(e) => maj('table', e.target.value)} className={ii}>
              <option value="">devoirs et examens</option>
              <option value="devoirs">devoirs</option>
              <option value="examens">examens</option>
            </select>
          </FF>
          <FF label="Opération">
            <select value={saisie.operation || ''} onChange={(e) => maj('operation', e.target.value)} className={ii}>
              <option value="">toutes</option>
              <option value="I">création</option>
              <option value="U">modification</option>
              <option value="D">suppression</option>
            </select>
          </FF>
          <FF label="Limite">
            <select value={saisie.limit || 500} onChange={(e) => maj('limit', e.target.value)} className={ii}>
              {[100, 500, 1000, 2000].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </FF>
        </div>
        <div className="mt-3 flex gap-2 justify-end flex-wrap">
          <button type="button" onClick={() => refetch()} disabled={isFetching} className={bouton}>
            <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} /> Rafraîchir
          </button>
          <button type="button" onClick={exportCSV} disabled={!items.length} className={bouton}>
            <Download size={14} /> CSV
          </button>
          <button type="submit" className="px-3 py-1.5 text-sm rounded bg-gray-900 hover:bg-gray-700 text-white dark:bg-gray-100 dark:hover:bg-gray-300 dark:text-gray-900">
            Rechercher
          </button>
        </div>
      </form>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
        <div className="p-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Journal des notes</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {data ? `${items.length} sur ${data.total}` : ''}
          </div>
        </div>
        {detail && <div className="p-4 text-sm text-red-700 dark:text-red-300">{detail}</div>}
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {!detail && items.length === 0 && (
            <li className="p-6 text-sm text-gray-500 dark:text-gray-400 text-center">
              Aucune écriture de note pour ces filtres. Le journal démarre à son installation, le 11/09/2026.
            </li>
          )}
          {items.map(l => <LigneJournalNote key={l.id} l={l} />)}
        </ul>
      </div>
    </div>
  );
}

function LigneJournalNote({ l }: { l: JournalNoteLigne }) {
  const d = new Date(l.date);
  const ton = l.operation === 'D'
    ? 'border-red-300 text-red-700 dark:border-red-800 dark:text-red-300'
    : l.operation === 'I'
      ? 'border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-300'
      : 'border-amber-300 text-amber-700 dark:border-amber-800 dark:text-amber-300';
  return (
    <li className="p-3">
      <div className="flex items-start gap-3 flex-wrap md:flex-nowrap">
        <div className="shrink-0 w-36 space-y-1">
          <div className="text-xs text-gray-600 dark:text-gray-300">
            {d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'medium' })}
          </div>
          <span className={`inline-block px-1.5 py-0.5 text-[11px] rounded border ${ton}`}>
            {LIBELLES_OPERATIONS[l.operation] || l.operation}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm text-gray-900 dark:text-gray-100">
            <span className="font-mono">{l.etudiant.matricule || `étudiant #${l.etudiant.id}`}</span>
            {l.etudiant.nom && <span> · {l.etudiant.nom}</span>}
            {l.etudiant.classe && <span className="text-gray-500 dark:text-gray-400"> · {l.etudiant.classe}</span>}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {l.table === 'devoir' ? 'Devoir' : (l.est_rattrapage ? 'Examen de rattrapage' : 'Examen')}
            {' · '}{l.matiere.code || `matière #${l.matiere.id}`} {l.matiere.nom}
            {l.semestre && <> · {l.semestre}</>}
            {l.note_id && <> · note #{l.note_id}</>}
          </div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs">
            {l.changements.map(c => (
              <span key={c.champ} className="text-gray-700 dark:text-gray-300">
                <span className="text-gray-500 dark:text-gray-400">{LIBELLES_CHAMPS[c.champ] || c.champ} : </span>
                {l.operation !== 'I' && (
                  <span className={l.operation === 'U' ? 'line-through text-gray-400' : ''}>{valeurJournal(c.champ, c.avant)}</span>
                )}
                {l.operation === 'U' && <span className="text-gray-400"> → </span>}
                {l.operation !== 'D' && (
                  <span className="font-medium text-gray-900 dark:text-gray-100">{valeurJournal(c.champ, c.apres)}</span>
                )}
              </span>
            ))}
          </div>
        </div>
        <div className="shrink-0 text-xs md:text-right text-gray-500 dark:text-gray-400 space-y-0.5 md:max-w-xs">
          <div className="text-gray-800 dark:text-gray-200">
            {l.utilisateur
              ? <>{l.utilisateur.nom || l.utilisateur.username} <span className="text-gray-500 dark:text-gray-400">({l.utilisateur.username} · {l.utilisateur.role})</span></>
              : <span className="italic">{l.origine || 'hors application'}</span>}
          </div>
          {l.adresse_ip && <div className="font-mono">{l.adresse_ip}</div>}
          {l.chemin && <div className="font-mono break-all">{l.chemin}</div>}
          {l.utilisateur && l.origine && l.origine !== 'http' && <div>{l.origine}</div>}
          {!l.utilisateur && l.utilisateur_db && <div>base : {l.utilisateur_db}</div>}
        </div>
      </div>
    </li>
  );
}

// ─── Vue utilisateur ────────────────────────────────────────────────────────

function VueUser({ userId }: { userId: number }) {
  const [depuis, setDepuis] = useState('');
  const [limit, setLimit] = useState(500);
  const { data, isFetching, refetch } = useAuditActiviteUserQuery({
    user_id: userId, depuis: depuis || undefined, limit,
  });
  const [openIds, setOpenIds] = useState<Record<number, boolean>>({});

  const items = data?.items || [];

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {data?.user?.nom_complet || `Utilisateur #${userId}`}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {data?.user ? `${data.user.username} · ${data.user.role}` : 'chargement…'}
            </div>
          </div>
          <div className="flex items-end gap-2 flex-wrap">
            <FF label="Depuis"><input type="date" value={depuis} onChange={(e) => setDepuis(e.target.value)} className={ii} /></FF>
            <FF label="Limite">
              <select value={limit} onChange={(e) => setLimit(Number(e.target.value))} className={ii}>
                {[200, 500, 1000, 2000].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </FF>
            <button onClick={() => refetch()} disabled={isFetching}
              className="mb-0.5 inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700">
              <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} /> Rafraîchir
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KpiCard label="Requêtes affichées" value={items.length} />
        <KpiCard label="Total sur la fenêtre" value={data?.total || 0} />
        <KpiCard label="IPs distinctes" value={(data?.ips || []).length}
          tone={(data?.ips || []).length > 5 ? 'warn' : 'neutral'}
          hint={(data?.ips || []).length > 5 ? 'Nombreuses IPs' : undefined} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Endpoints les plus visités</div>
          <div className="space-y-1">
            {(data?.top_paths || []).map((p, i) => (
              <div key={i} className="flex items-center justify-between gap-2 px-2 py-1">
                <div className="text-xs font-mono text-gray-800 dark:text-gray-200 truncate">{p.path}</div>
                <div className="text-xs font-semibold text-gray-600 dark:text-gray-400">{p.nb.toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="font-semibold text-gray-900 dark:text-gray-100 mb-3">IPs & user-agents</div>
          <div className="space-y-2">
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">IPs distinctes</div>
              {(data?.ips || []).map((p, i) => (
                <div key={i} className="flex items-center justify-between gap-2 px-2 py-0.5">
                  <div className="text-xs font-mono text-gray-800 dark:text-gray-200">{p.ip}</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">{p.nb.toLocaleString()}</div>
                </div>
              ))}
            </div>
            <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">User-agents</div>
              {(data?.user_agents || []).map((u, i) => (
                <div key={i} className="flex items-start justify-between gap-2 px-2 py-0.5">
                  <div className="text-xs font-mono text-gray-800 dark:text-gray-200 truncate flex-1">{u.ua}</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 shrink-0">{u.nb.toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
        <div className="p-3 border-b border-gray-100 dark:border-gray-700 text-sm font-semibold text-gray-900 dark:text-gray-100">
          Timeline
        </div>
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {items.length === 0 && (
            <li className="p-6 text-sm text-gray-500 dark:text-gray-400 text-center">
              Aucune activité journalisée pour cet utilisateur.
            </li>
          )}
          {items.map(l => (
            <LigneJournal key={l.id} l={l}
              open={!!openIds[l.id]}
              onToggle={() => setOpenIds(s => ({ ...s, [l.id]: !s[l.id] }))}
              onOpenUser={() => { /* déjà cette page */ }}
            />
          ))}
        </ul>
      </div>
    </div>
  );
}

// ─── Petits helpers stylés ─────────────────────────────────────────────────

const ii = 'w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100';

function FF({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1" dangerouslySetInnerHTML={{ __html: label }} />
      {children}
    </div>
  );
}

export default AuditAgents;
